import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const SAGE = '#7C9A82';
const AMBER = '#D4A574';

// Formats a number to two decimals when finite, or an em-dash placeholder
// when the aggregate has no data yet (e.g. every echo missing that mood).
const fmt = (n, digits = 2) =>
  Number.isFinite(n) ? n.toFixed(digits) : '—';

// Compact card body used on both variants. Mobile drops the subtitle (there's
// no room on a 1/3-width tile) and stacks emoji + number vertically so the
// number reads as the hero. Desktop keeps the original horizontal layout.
function CardBody({ label, value, suffix, subtitle, emoji, trailing, tone = 'light' }) {
  const labelCls =
    tone === 'light'
      ? 'text-stone-400 dark:text-stone-500'
      : 'text-white/80';
  const numberCls =
    tone === 'light'
      ? 'text-stone-800 dark:text-[#F0EDE8]'
      : 'text-white';
  const suffixCls =
    tone === 'light'
      ? 'text-stone-400 dark:text-stone-500'
      : 'text-white/70';
  const subtitleCls =
    tone === 'light'
      ? 'text-stone-500 dark:text-stone-400'
      : 'text-white/85';
  const emojiChipCls =
    tone === 'light'
      ? 'bg-stone-100 dark:bg-stone-700/60'
      : 'bg-white/25';

  return (
    <div className="relative">
      <div
        className={`text-[9px] sm:text-[11px] uppercase tracking-[0.14em] font-semibold ${labelCls}`}
      >
        {label}
      </div>

      {/* Mobile: emoji sits on its own row above the number so the number can
          grow to full card width. Desktop: emoji + number share a baseline. */}
      <div className="mt-1.5 sm:mt-3 flex flex-col sm:flex-row sm:items-baseline sm:gap-2 min-w-0">
        {emoji && (
          <span
            aria-hidden="true"
            className={`inline-flex h-6 w-6 sm:h-9 sm:w-9 items-center justify-center rounded-full ${emojiChipCls} text-sm sm:text-xl leading-none shrink-0 mb-1 sm:mb-0`}
          >
            {emoji}
          </span>
        )}
        <div className="flex items-baseline gap-0.5 sm:gap-1 min-w-0">
          <span
            className={`text-xl sm:text-[2.25rem] font-semibold tracking-tight ${numberCls} leading-none tabular-nums`}
          >
            {value}
          </span>
          {suffix && (
            <span
              className={`text-[10px] sm:text-sm font-medium ${suffixCls} leading-none`}
            >
              {suffix}
            </span>
          )}
        </div>
        {trailing && (
          <div className="hidden sm:block sm:ml-auto shrink-0">{trailing}</div>
        )}
      </div>

      {/* Subtitle: hidden on mobile to preserve card height. */}
      <div className={`hidden sm:block mt-2 text-sm ${subtitleCls}`}>
        {subtitle}
      </div>
    </div>
  );
}

function StatCard(props) {
  return (
    <div
      data-testid="kpi-card"
      className="min-w-0 rounded-2xl p-3 sm:p-6 bg-white dark:bg-stone-800/40 shadow-sm border border-stone-200/60 dark:border-stone-700/40 transition-shadow duration-300 ease-out hover:shadow-md"
    >
      <CardBody {...props} />
    </div>
  );
}

function FeaturedStatCard(props) {
  return (
    <div
      data-testid="kpi-card-featured"
      className="relative min-w-0 rounded-2xl p-3 sm:p-6 shadow-md transition-shadow duration-300 ease-out overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, ${SAGE} 0%, #8FA890 55%, #A9C4AF 100%)`,
      }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-10 -right-10 h-24 w-24 sm:h-32 sm:w-32 rounded-full opacity-25"
        style={{ backgroundColor: '#F0EDE8' }}
      />
      <CardBody {...props} tone="dark" />
    </div>
  );
}

function ShiftBadge({ shift }) {
  if (!Number.isFinite(shift)) {
    return (
      <span
        aria-hidden="true"
        className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold bg-stone-100 text-stone-500 dark:bg-stone-700/60 dark:text-stone-300"
      >
        <Minus className="h-3 w-3" />
      </span>
    );
  }
  const positive = shift > 0;
  const flat = shift === 0;
  const bg = flat
    ? 'bg-stone-100 text-stone-500 dark:bg-stone-700/60 dark:text-stone-300'
    : 'text-white';
  const style = flat
    ? undefined
    : positive
      ? { backgroundColor: SAGE }
      : { backgroundColor: AMBER };
  const Icon = flat ? Minus : positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${bg}`}
      style={style}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span className="tabular-nums">
        {positive ? '+' : ''}
        {shift.toFixed(1)}
      </span>
    </span>
  );
}

export default function KpiRow({ avgPre, avgPost, avgShift }) {
  const shiftSuffix = Number.isFinite(avgShift) ? 'points' : '';
  const shiftValue = Number.isFinite(avgShift)
    ? `${avgShift > 0 ? '+' : ''}${avgShift.toFixed(1)}`
    : '—';
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-4">
      <StatCard
        label="Pre-Echo"
        value={fmt(avgPre)}
        suffix="/10"
        subtitle="Baseline sentiment"
        emoji="🤔"
      />
      <FeaturedStatCard
        label="Post-Echo"
        value={fmt(avgPost)}
        suffix="/10"
        subtitle="Resolved sentiment"
        emoji="🙂"
      />
      <StatCard
        label="Shift"
        value={shiftValue}
        suffix={shiftSuffix}
        subtitle="Improvement per session"
        trailing={<ShiftBadge shift={avgShift} />}
      />
    </div>
  );
}
